<template>
  <div
    class="cursor-pointer h-full shadow-md rounded-md bg-white flex flex-col"
    :class="{ 'bg-gradient-to-br from-[#ECECFE] to-[#D3D5FD]': detail?.is_vip }"
  >
    <div class="h-64 w-full relative bg-white rounded-t-md overflow-hidden">
      <img
        :key="`${detail?.id}-${safeThumbnail}`"
        :src="safeThumbnail"
        class="h-full w-full object-fill"
        alt=""
        @error="onImgError"
      />
      <p
        v-if="detail?.status == 2"
        class="bg-red-500 absolute bottom-0 left-0 py-1 px-2 text-white font-medium w-full"
      >
        {{ $t('page.expired') }}
      </p>
      <p
        v-if="detail?.add_home"
        class="bg-red-500 absolute bottom-0 py-1 px-2 text-white font-bold text-xl right-0 w-max"
      >
        B
      </p>
      <template v-if="detail?.has_video">
        <div class="absolute inset-0 bg-[rgba(0,0,0,0.40)]"></div>
        <IconPlay class="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[1]">
        </IconPlay>
      </template>
    </div>
    <div class="-mt-3.5 self-end mr-5 z-20 relative">
      <VueStarRating
        :rounded-corners="true"
        :star-size="15"
        active-color="#ffb300"
        inactive-color="#f2f2f2"
        :border-width="3"
        border-color="#ffb300"
        :show-rating="false"
        :read-only="true"
        :increment="0.5"
        :rating="detail?.rating"
      ></VueStarRating>
    </div>

    <div
      class="mx-3 space-y-2 my-3 min-w-0"
      :class="{ 'bg-gradient-to-br from-[#ECECFE] to-[#D3D5FD]': detail?.is_vip }"
    >
      <!-- TITLE (устойчиво: 1 строка + ... + год справа если есть) -->
      <div class="flex items-center gap-2 min-w-0">
        <p
          class="flex-1 min-w-0 text-main-color font-bold text-sm truncate"
          :title="getTitleFull()"
        >
          {{ getTitleMain() }}
        </p>

        <span
          v-if="getTitleRight()"
          class="shrink-0 text-main-color font-bold text-sm"
          :title="getTitleRight()"
        >
          {{ getTitleRight() }}
        </span>
      </div>

      <template v-if="detail?.business_account">
        <router-link
          class="block w-full max-w-full bg-main-color text-white px-2 py-0.5 text-base truncate"
          :title="detail.business_account.name"
          :to="{ name: 'businessAccountsDetail', params: { id: detail.business_account.id } }"
        >
          {{ detail.business_account.name }}
        </router-link>
      </template>
      <p class="text-black truncate" :title="detail?.location">
        {{ detail?.location }}
      </p>
      <div class="flex justify-between items-center">
        <p
          v-if="has_price && detail?.price != null && detail?.price !== ''"
          class="text-[#409DBA] font-bold text-sm truncate"
        >
          {{ formatNumber(detail.price) }} TMT
        </p>
        <p class="text-sm truncate max-w-[130px]">
          {{ detail?.date_created }}
        </p>
      </div>
      <div class="flex justify-between mt-3">
        <div class="flex items-center gap-2">
          {{ $t('page.onCredit') }} <IconChecked v-if="detail?.on_credit" />
          <IconUnchecked v-else />
        </div>
        <div class="flex items-center gap-2">
          {{ $t('page.onExchange') }} <IconChecked v-if="detail?.on_exchange" />
          <IconUnchecked v-else />
        </div>
      </div>
    </div>
    <div class="mt-auto w-full">
      <div v-if="!detail?.is_vip && detail?.subscription == ''" class="py-5"></div>
      <div
        v-if="detail?.is_vip"
        class="w-max ml-auto px-3 bg-[#7951F9] text-white font-bold text-2xl text-end"
      >
        VIP
      </div>
      <button
        v-if="detail?.subscription !== ''"
        class="w-full rounded-b-md py-3 font-bold"
        :class="getSubcriptionType?.color"
      >
        {{ getSubcriptionType?.name }}
      </button>
    </div>
  </div>
</template>

<script>
import IconChecked from '@icons/IconChecked.vue';
import IconUnchecked from '@icons/IconUnchecked.vue';
import { formatNumber } from '@/helpers';
import VueStarRating from 'vue-star-rating';
import IconPlay from '@icons/IconPlay.vue';

export default {
  name: 'PublicationCard',
  components: { IconUnchecked, IconChecked, VueStarRating, IconPlay },
  props: {
    detail: { type: Object, required: true },
    has_name: { type: Boolean, default: false },
    has_price: { type: Boolean, default: true },
  },

  computed: {
    titleParts() {
      const lang = String(this.$i18n?.locale || '').toLowerCase();

      const titleStr =
        lang === 'ru'
          ? this.detail?.title_ru
          : lang === 'tk'
            ? this.detail?.title_tk
            : this.detail?.title;

      const t = String(titleStr ?? '').trim();
      if (t) return t.split(/\s+/).filter(Boolean);

      const arr = Array.isArray(this.detail?.title_properties) ? this.detail.title_properties : [];
      const props = arr
        .map(p => p?.value)
        .filter(Boolean)
        .map(v => String(v).trim())
        .filter(Boolean);

      if (props.length) return props;

      const n = String(this.detail?.name ?? '').trim();
      return n ? [n] : [];
    },

    safeThumbnail() {
      const thumb = String(this.detail?.thumbnail ?? '').trim();

      if (!thumb || thumb === 'None' || thumb === 'null' || thumb === 'undefined') {
        return '/placeholder-publication.png';
      }

      return thumb;
    },

    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          return null;
      }
    },
  },

  methods: {
    formatNumber,

    onImgError(event) {
      if (!event?.target) return;

      const fallback = '/placeholder-publication.png';

      if (!event.target.src.includes(fallback)) {
        event.target.src = fallback;
      }
    },

    isYearLike(v) {
      const s = String(v ?? '').trim();
      return /^\d{4}$/.test(s);
    },

    getTitleMain() {
      const parts = this.titleParts;
      if (parts.length >= 2 && this.isYearLike(parts[parts.length - 1])) {
        return parts.slice(0, -1).join(' ');
      }
      return parts.join(' ');
    },

    getTitleRight() {
      const parts = this.titleParts;
      if (parts.length >= 2 && this.isYearLike(parts[parts.length - 1])) {
        return parts[parts.length - 1];
      }
      return '';
    },

    getTitleFull() {
      return this.titleParts.join(' ');
    },
  },
};
</script>

<style scoped></style>
